import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AuthoritiesEnum, IntegrationTypeEnum } from "~/types/enums";
import { Alert, Badge, Box, Button, Card, Grid, Icon, InputCheckbox, InputText, InputToggle, Link, Picture, Spinner, Text } from "~/components-shared";
import integrationsActionCreators from "~/store/integrations/actions";
import integrationSelectors from "~/store/integrations/selectors";
import useAbacExt from "~/hooks/abac";
import api from "~/lib/api";
import { getMondaySpaceUrl, handleError, helpers } from "~/lib/helpers";
import styled from "styled-components";
import mondayLogo from "~/assets/i/apps/monday_logo.svg";
import { useTranslation } from "react-i18next";
import lists from "~/lib/lists";
const Actions = styled.div`
  position: relative;
  z-index: 2;
`;
const StyledBox = styled(Box)`
  position: relative;
  z-index: 2;
  max-height: 250px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
`;
const authorities = [AuthoritiesEnum.INTEGRATIONS_WRITE, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE, AuthoritiesEnum.SIGN_UP_INTEGRATE];
const MondayIntegrationCard = _ref => {
  let {
    ...rest
  } = _ref;
  const {
    t
  } = useTranslation();
  const {
    userHasOneOf
  } = useAbacExt();
  const dispatch = useDispatch();
  const integration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.MONDAY));
  const [mondayBoards, setMondayBoards] = useState([]);
  const [loadingMondayBoards, setLoadingMondayBoards] = useState(false);
  const [mondayBoardsError, setMondayBoardsError] = useState();
  const [isExpanded, setIsExpanded] = useState(false);
  const [search, setSearch] = useState("");
  const handleLoadMondayBoards = () => {
    setLoadingMondayBoards(true);
    setMondayBoardsError(undefined);
    api.fetchMondayBoards().then(data => setMondayBoards(data)).catch(e => setMondayBoardsError(handleError(e))).finally(() => setLoadingMondayBoards(false));
  };
  useEffect(() => {
    if (integration && userHasOneOf(authorities)) {
      handleLoadMondayBoards();
    }
  }, [!!integration]);
  const handleToggle = value => {
    if (value) {
      api.mondayInstall().then(_ref2 => {
        let {
          path
        } = _ref2;
        const width = 560;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;
        window.open(path, "Install Monday", `width=${width},height=${height},left=${left},top=${top}`);
      });
      setIsExpanded(true);
    } else if (integration?.id) {
      dispatch(integrationsActionCreators.deleteStarted(integration.id));
    }
  };
  const handleExpand = () => {
    setIsExpanded(prev => !prev);
  };
  const handleUpdate = function (boardIds) {
    let force = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    let deselect = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    if (integration) {
      let boards = integration.settings?.boards || [];
      boardIds.forEach(boardId => {
        if (boards.includes(boardId) && !force || deselect && force) {
          boards = boards.filter(id => id !== boardId);
        } else {
          boards.push(boardId);
        }
      });
      dispatch(integrationsActionCreators.updateStarted(integration.id, {
        type: integration.type,
        token: integration.token,
        settings: {
          ...integration.settings,
          boards
        }
      }));
    }
  };
  const handleSearch = value => {
    setSearch(value);
  };
  const filteredBoards = mondayBoards.filter(board => board.name.toLowerCase().includes(search.toLowerCase()) || board.description && board.description.toLowerCase().includes(search.toLowerCase()) || board.workspace.name.toLowerCase().includes(search.toLowerCase()));
  const hasFiltered = filteredBoards.length && search;
  const groupedBoards = helpers.groupBy(filteredBoards, "workspace.id");
  return /*#__PURE__*/React.createElement(Card, _extends({
    onClick: userHasOneOf(authorities) && integration ? handleExpand : undefined,
    elemBefore: /*#__PURE__*/React.createElement(Picture, {
      src: mondayLogo,
      width: "30px",
      height: "30px",
      border: "squared",
      color: "transparent",
      s: {
        mr: 2
      }
    }),
    title: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      element: "h3",
      display: "inline-block"
    }, "Monday"), integration && userHasOneOf(authorities) && /*#__PURE__*/React.createElement(Icon, {
      scale: 4,
      src: "chevron",
      rotate: isExpanded ? 0 : 180,
      s: {
        ml: 1,
        mb: 1
      }
    })),
    elemAfter: /*#__PURE__*/React.createElement(Actions, null, /*#__PURE__*/React.createElement(InputToggle, {
      value: !!integration,
      display: "inline-block",
      onChange: (value, event) => {
        event.stopPropagation();
        handleToggle(value);
      },
      disabled: !userHasOneOf(authorities)
    })),
    collapsed: !integration || !isExpanded,
    s: {
      mb: 3
    }
  }, rest), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    fontSize: 4,
    lineHeight: 6,
    s: {
      mb: 3
    }
  }, t("CompanyIntegrations.connectedTo"), " ", /*#__PURE__*/React.createElement(Link, {
    color: "primary",
    href: integration ? getMondaySpaceUrl(integration) : "",
    underline: true
  }, integration?.settings?.account?.name), " ", t("CompanyIntegrations.workspace"), ". Select the board(s) you want us to summarize and map:"), /*#__PURE__*/React.createElement(InputText, {
    onClick: e => e.stopPropagation(),
    s: {
      mb: 4
    },
    value: search,
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "search",
      s: {
        mr: 2
      }
    }),
    placeholder: t("common.actions.search"),
    onChange: val => handleSearch(val),
    elemAfter: !!search && /*#__PURE__*/React.createElement(Box, {
      gap: 1,
      s: {
        ml: 1
      }
    }, /*#__PURE__*/React.createElement(Button, {
      onClick: e => {
        e.stopPropagation();
        handleUpdate(filteredBoards.map(r => r.name), true);
      },
      color: "translucent",
      outline: false,
      fontSize: 3,
      s: {
        px: 3,
        py: 1
      },
      rounded: true
    }, hasFiltered ? "Select found" : "Select all"), /*#__PURE__*/React.createElement(Button, {
      onClick: e => {
        e.stopPropagation();
        handleUpdate(filteredBoards.map(r => r.name), true, true);
      },
      color: "translucent",
      fontSize: 3,
      outline: false,
      s: {
        px: 3,
        py: 1
      },
      rounded: true
    }, hasFiltered ? "Deselect found" : "Deselect all")),
    clearButton: true,
    fullWidth: true
  }), mondayBoardsError && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, mondayBoardsError), loadingMondayBoards ? /*#__PURE__*/React.createElement(Spinner, {
    center: true
  }) : Object.keys(groupedBoards).length ? /*#__PURE__*/React.createElement(StyledBox, {
    direction: "row",
    s: {
      pt: 2,
      pr: 2
    },
    fullWidth: true
  }, Object.keys(groupedBoards).map(key => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    template: "1rem 1fr auto",
    gap: 3,
    s: {
      mb: 5
    },
    fullWidth: true
  }, /*#__PURE__*/React.createElement(InputCheckbox, {
    value: groupedBoards[key].map(board => board.id).every(v => integration?.settings?.boards.includes(v)),
    onChange: () => handleUpdate(groupedBoards[key].map(board => board.id), !groupedBoards[key].map(board => board.id).every(v => integration?.settings?.boards.includes(v))),
    s: {
      mt: -2
    }
  }), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    fontSize: 4,
    weight: "semiBold"
  }, groupedBoards[key][0].workspace.name)), /*#__PURE__*/React.createElement(Grid.Cell, {
    justify: "end"
  }, /*#__PURE__*/React.createElement(Badge, {
    color: lists.monday.workspaceKinds.colors[groupedBoards[key][0].workspace.kind],
    s: {
      px: 2.5
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    src: lists.monday.workspaceKinds.icons[groupedBoards[key][0].workspace.kind],
    s: {
      mr: 1,
      mt: -0.5
    }
  }), lists.monday.workspaceKinds.names[groupedBoards[key][0].workspace.kind]))), /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    template: "1rem 1fr auto",
    s: {
      pl: 5,
      mb: 5
    },
    gap: 3,
    justify: "center",
    fullWidth: true
  }, groupedBoards[key].map(board => /*#__PURE__*/React.createElement(React.Fragment, {
    key: board.id
  }, /*#__PURE__*/React.createElement(InputCheckbox, {
    value: integration?.settings?.boards?.includes(board.id),
    onChange: () => handleUpdate([board.id]),
    s: {
      mt: -2
    }
  }), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Link, {
    href: board.url,
    display: "block",
    color: "primary",
    fontSize: 4,
    s: {
      mb: 0.5
    },
    weight: "semiBold"
  }, board.name), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "semiBold",
    color: "textSecondary"
  }, "Owned by ", [...board.owners, ...board.teamOwners].map(owner => owner.name).join(", "), board.hasSubBoard && ` • Has Subitems`)), /*#__PURE__*/React.createElement(Grid.Cell, {
    justify: "end"
  }, /*#__PURE__*/React.createElement(Badge, {
    color: lists.monday.boardKinds.colors[board.kind],
    s: {
      px: 2.5
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    src: lists.monday.boardKinds.icons[board.kind],
    s: {
      mr: 1,
      mt: -0.5
    }
  }), lists.monday.boardKinds.names[board.kind])))))))) : /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    weight: "semiBold",
    s: {
      my: 2
    },
    align: "center"
  }, "No boards found"));
};
export default MondayIntegrationCard;